import type { MapSnapshot } from '../types'

// VerseCloud uses an intentionally hand-authored silhouette as its published
// artistic interpretation of the Tang realm. It communicates a poetic spatial
// impression rather than claiming to be a scholarly border reconstruction.
const tangPrototypeBoundary: number[][][][] = [
  [
    [
      [73.5, 39.2],
      [78, 43.2],
      [84.5, 46.5],
      [91.5, 47.6],
      [99, 45.8],
      [105.5, 43.8],
      [113, 44.9],
      [120.4, 42.4],
      [123.8, 38.8],
      [121.5, 34.2],
      [121.2, 29.5],
      [117.8, 24.2],
      [111.2, 20.8],
      [105.2, 22.3],
      [100.2, 25.4],
      [96.4, 28.4],
      [91.4, 29.8],
      [86, 33.2],
      [80, 35.1],
      [73.5, 39.2],
    ],
  ],
]

export const snapshots: MapSnapshot[] = [
  {
    id: 'pre-qin-literary-period',
    dynasty: 'pre-qin',
    dynastyLabel: '先秦',
    eraLabel: '诗经至楚辞',
    year: -500,
    startYear: -1100,
    endYear: -221,
    dateRange: '约前11世纪—前221',
    status: 'published',
    note: '以《诗经》、楚辞及先秦歌谣为线索；每篇地点均区分作品场景与具体传承关联地。',
  },
  {
    id: 'han-literary-period',
    dynasty: 'han',
    dynastyLabel: '汉',
    eraLabel: '两汉乐府',
    year: 100,
    startYear: -206,
    endYear: 220,
    dateRange: '前206—220',
    status: 'published',
    note: '覆盖两汉乐府与文人歌诗。',
  },
  {
    id: 'wei-jin-literary-period',
    dynasty: 'wei-jin',
    dynastyLabel: '魏晋',
    eraLabel: '建安至东晋',
    year: 350,
    startYear: 220,
    endYear: 420,
    dateRange: '220—420',
    status: 'published',
    note: '覆盖建安、曹魏、西晋与东晋诗歌。',
  },
  {
    id: 'southern-northern-literary-period',
    dynasty: 'southern-northern',
    dynastyLabel: '南北朝',
    eraLabel: '南朝与北朝',
    year: 500,
    startYear: 420,
    endYear: 589,
    dateRange: '420—589',
    status: 'published',
    note: '并置南朝山水诗与北朝民歌。',
  },
  {
    id: 'sui-literary-period',
    dynasty: 'sui',
    dynastyLabel: '隋',
    eraLabel: '开皇至大业',
    year: 600,
    startYear: 581,
    endYear: 618,
    dateRange: '581—618',
    status: 'published',
    note: '短暂统一时代的宫体、行旅与民歌。',
  },
  {
    id: 'tang-742-concept',
    dynasty: 'tang',
    dynastyLabel: '唐',
    eraLabel: '天宝元年',
    year: 742,
    startYear: 618,
    endYear: 907,
    dateRange: '618—907',
    status: 'published',
    note: '盛唐概念疆域 · 艺术化空间演绎',
    boundary: tangPrototypeBoundary,
  },
  {
    id: 'five-dynasties-literary-period',
    dynasty: 'five-dynasties',
    dynastyLabel: '五代',
    eraLabel: '五代十国',
    year: 950,
    startYear: 907,
    endYear: 978,
    dateRange: '907—978（含李煜降宋后词）',
    status: 'published',
    note: '以南唐词为核心，含李煜降宋后的囚居词，呈现诗向词的转折。',
  },
  {
    id: 'song-literary-period',
    dynasty: 'song',
    dynastyLabel: '宋',
    eraLabel: '元丰三年',
    year: 1080,
    startYear: 960,
    endYear: 1279,
    dateRange: '960—1279',
    status: 'published',
    note: '覆盖北宋与南宋诗词。',
  },
  {
    id: 'yuan-literary-period',
    dynasty: 'yuan',
    dynastyLabel: '元',
    eraLabel: '至元十七年',
    year: 1280,
    startYear: 1271,
    endYear: 1368,
    dateRange: '1271—1368',
    status: 'published',
    note: '以散曲、题画诗和怀古作品为线索。',
  },
  {
    id: 'ming-literary-period',
    dynasty: 'ming',
    dynastyLabel: '明',
    eraLabel: '洪武二十四年',
    year: 1391,
    startYear: 1368,
    endYear: 1647,
    dateRange: '1368—1647（含南明遗民）',
    status: 'published',
    note: '覆盖明代咏物、怀古与文人日常，并明确标注南明遗民作品。',
  },
  {
    id: 'qing-literary-period',
    dynasty: 'qing',
    dynastyLabel: '清',
    eraLabel: '清代',
    year: 1750,
    startYear: 1644,
    endYear: 1912,
    dateRange: '1644—1912',
    status: 'published',
    note: '覆盖清代性灵诗、题画诗与近代变革之声。',
  },
]

export const activeSnapshot = snapshots.find((snapshot) => snapshot.dynasty === 'tang')
  ?? snapshots[0]

export const dynastyLabels = Object.fromEntries(
  snapshots.map((snapshot) => [snapshot.dynasty, snapshot.dynastyLabel]),
) as Record<MapSnapshot['dynasty'], string>
